import { gql } from '@apollo/client/core'
import { print, Source } from 'graphql/language'

const inlineSpreadFragments = (fragmentDefinitions, definition) => {
  if (definition.kind === 'FragmentSpread') {
    return fragmentDefinitions.find(
      ({ name }) => name.value === definition.name.value,
    )
  }

  if (definition.selectionSet) {
    definition.selectionSet = {
      ...definition.selectionSet,
      selections: definition.selectionSet.selections.map(selection =>
        inlineSpreadFragments(fragmentDefinitions, selection),
      )
    }
  }

  return definition
}

const createFragmentMap = (doc) => {
  const definitions = doc.definitions.map(definition => {
    if (definition.kind === 'FragmentDefinition') {
      return {
        kind: 'InlineFragment',
        name: definition.name,
        directives: definition.directives,
        selectionSet: definition.selectionSet,
        typeCondition: definition.typeCondition,
      }
    }
  })

  return definitions.reduce((result, definition) => {
    if (definition) {
      const nval = definition.name.value
      const name = nval.split('_')[1] || nval
      const ndoc = {
        ...doc,
        definitions: [
          inlineSpreadFragments(definitions, definition)
        ]
      }

      ndoc.loc = {
        ...doc.loc,
        source: new Source(print(ndoc))
      }

      result[name] = ndoc
    }

    return result
  }, {})
}

const getFragment = ({ type, name, definition, doc }) => {
  const valid = ['Collection', 'Record', 'Create', 'Update', 'Destroy']

  if (valid.includes(type)) {
    const map = createFragmentMap(doc)
    const key = definition || `${name}${type}`

    return map[key] || map[name]
  }
}

export function generateQuery(type, model, doc, definition) {
  const { name, pluralName, slug, pluralSlug } = model

  const fragment = getFragment({ type, name, definition, doc })

  if (type == 'Collection') {
    return gql`
      query ${pluralName}Connection($first: Int, $skip: Int, $orderBy: ${name}OrderByInput, $where: ${name}WhereInput) {
        ${pluralSlug}Connection(first: $first, skip: $skip, orderBy: $orderBy, where: $where) {
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          total
          nodes {
            ${fragment}
          }
        }
      }
    `
  }

  if (type == 'Record') {
    return gql`
      query ${name}($where: ${name}WhereUniqueInput!) {
        ${slug}(where: $where) {
          ${fragment}
        }
      }
    `
  }

  return doc[type] || {}
}

export function generateMutation(type, model, doc, definition) {
  const { name, slug } = model

  const response = 'status notice errors messages'
  const fragment = getFragment({ type, name, definition, doc })

  if (type == 'Create') {
    return gql`
      mutation Create${name}($input: Create${name}Input!) {
        create${name}(input: $input) {
          ${response}
          ${slug} {
            ${fragment}
          }
        }
      }
    `
  }

  if (type == 'Update') {
    return gql`
      mutation Update${name}($input: Update${name}Input!) {
        update${name}(input: $input) {
          ${response}
          ${slug} {
            ${fragment}
          }
        }
      }
    `
  }

  if (type == 'Destroy') {
    return gql`
      mutation Destroy${name}($input: Destroy${name}Input!) {
        destroy${name}(input: $input) {
          ${response}
        }
      }
    `
  }

  return doc[type] || {}
}

export function generateSubscription(type, model, doc, definition) {
  const { name, slug } = model

  const fragment = getFragment({ type, name, definition, doc })

  return doc[type] || gql`
    subscription ${name} {
      ${slug} {
        ${fragment}
      }
    }
  `
}
